// src/pages/NewsPage.jsx
import React, { useState, useEffect } from "react";
import PageBanner from "../components/common/PageBanner";
import NewsContent from "../components/news/NewsContent";
import { useLocale } from "../contexts/LocaleContext";
import { getNewsPageData } from "../api/apiService";

const NewsPage = () => {
  const { locale } = useLocale();

  const [newsPageData, setNewsPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNewsContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getNewsPageData(locale);
        setNewsPageData(data);
      } catch (err) {
        // Normalize timeout error message for better UX
        const isTimeoutError =
          err?.code === "ECONNABORTED" ||
          (typeof err?.message === "string" &&
            err.message.toLowerCase().includes("timeout"));

        if (isTimeoutError) {
          setError("News server is taking too long to respond. Please try again in a moment.");
        } else {
          setError(err?.message || "Failed to load content.");
        }
        setNewsPageData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsContent();
  }, [locale]);

  const banner = newsPageData?.banner;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }

  if (!newsPageData) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">Content not available.</p>
      </div>
    );
  }

  const bannerTitle = banner?.title || "News";
  const bannerImageUrl = banner?.image?.[0]?.url;

  return (
    <div className="news-page min-h-screen">
      {/* Banner Section */}
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={banner?.titleColorIsWhite ?? true}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-56 sm:h-64 md:h-72 lg:h-80"
      />

      {/* Main Content Section */}
      <NewsContent data={newsPageData} />
    </div>
  );
};

export default NewsPage;
